import { AnyFinancialEntity } from '@common/financialEntities';
import { Goal } from '@common/goals';
import { useCurrentUser } from '@common/react/user';
import { dt } from '@common/util/dateTime';
import { isNotNullish, isNumber } from '@common/util/types';
import { findValueNode, makeTick, TickRange } from '@common/valueSeries';
import { useChartRangeControls } from '../common/charts/ChartRangeControls';
import { configByGoalType } from './config';
import { GoalsChartCallout, GoalsChartEntityParams } from './GoalsChart';

export const getCallout = ({
    goal,
    targetDate,
    range,
    valueSeries,
}: {
    goal: Goal;
    /* fixed date for targeted goals, floating date for untargeted goals */
    targetDate: string;
    range: TickRange;
    valueSeries: GoalsChartEntityParams['valueSeries'];
}): GoalsChartCallout | undefined => {
    const targetTick = makeTick(targetDate, range.unit);
    const targetAmountForCallout =
        goal.targetAmount ?? findValueNode(valueSeries, targetTick).balance;
    if (isNumber(targetAmountForCallout)) {
        const config = configByGoalType[goal.type];
        return {
            iconName: config.iconName,
            color: config.color,
            date: makeTick(targetDate, range.unit).date,
            value: targetAmountForCallout,
        };
    }
};

export const useGoalDetailChartRangeControls = ({
    accounts,
}: {
    accounts: AnyFinancialEntity[];
}) => {
    const { userInfo } = useCurrentUser();
    const earliestDateTime = accounts
        .map((a) => a?.earliestSnapshot?.dateTime)
        .filter(isNotNullish)
        .sort()
        .at(0);
    const minDate = dt(earliestDateTime).toDateStr();
    const maxDate = userInfo.finalDate;

    const { chartRangeType, range, onIncrement, onDecrement, onChangeType } =
        useChartRangeControls({
            initialType: '1Y',
            minDate,
            maxDate,
            key: 'goal-detail',
        });

    return { chartRangeType, range, onIncrement, onDecrement, onChangeType };
};
